import React, { useState, useEffect } from 'react';
import { adminUsersAPI, authAPI } from '../services/api';

function Permissions() {
  const [adminUsers, setAdminUsers] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);
  const [allPermissions, setAllPermissions] = useState({});
  const [permissionChanges, setPermissionChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchAdminUsers();
    fetchAllPermissions();
  }, []);

  const fetchAdminUsers = async () => {
    setLoading(true);
    try {
      const response = await adminUsersAPI.getAdminUsers({ limit: 100 });
      setAdminUsers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching admin users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllPermissions = async () => {
    try {
      const response = await authAPI.getAllPermissions();
      setAllPermissions(response.data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  const selectAdmin = async (admin) => {
    setSelectedAdmin(admin);
    setMessage({ type: '', text: '' });
    
    try {
      // Fetch user's permissions
      const permResponse = await adminUsersAPI.getAdminPermissions(admin.id);
      setUserPermissions(permResponse.data);
      
      // Fetch permission change history
      const changesResponse = await adminUsersAPI.getPermissionChangeHistory(admin.id);
      setPermissionChanges(changesResponse.data.changes || []);
    } catch (error) {
      console.error('Error fetching user permissions:', error);
    }
  };

  const handleTogglePermission = async (permissionKey, isGranted) => {
    if (!selectedAdmin) return;

    const reason = prompt(
      isGranted 
        ? `Enter reason for revoking "${permissionKey}" (required for audit):` 
        : `Enter reason for granting "${permissionKey}" (optional):`
    );

    if (isGranted && !reason) {
      setMessage({ type: 'error', text: 'Reason required for revoking permissions' });
      return;
    }

    try {
      if (isGranted) {
        await adminUsersAPI.revokePermission(selectedAdmin.id, permissionKey, reason);
        setMessage({ type: 'success', text: `Permission revoked successfully` });
      } else {
        await adminUsersAPI.grantPermission(selectedAdmin.id, permissionKey, reason);
        setMessage({ type: 'success', text: `Permission granted successfully` });
      }
      
      // Refresh permissions
      selectAdmin(selectedAdmin);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to update permission' 
      });
    }
  };

  const handleBulkGrant = async (category) => {
    if (!selectedAdmin) return;

    const categoryPerms = allPermissions[category] || [];
    const permissionKeys = categoryPerms.map(p => p.key);

    const reason = prompt(`Grant all ${category} permissions? Enter reason:`);
    if (!reason) return;

    try {
      await adminUsersAPI.bulkGrantPermissions(selectedAdmin.id, permissionKeys, reason);
      setMessage({ type: 'success', text: `Granted ${permissionKeys.length} permissions` });
      selectAdmin(selectedAdmin);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to grant permissions' 
      });
    }
  };

  const categoryColors = {
    users: '#3498db',
    devices: '#2ecc71',
    serial_keys: '#e74c3c',
    notifications: '#f39c12',
    banners: '#9b59b6',
    faqs: '#1abc9c',
    policies: '#34495e',
    admin: '#e67e22',
    logs: '#95a5a6',
  };

  if (loading) {
    return <div className="loading">Loading permissions management...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1>Permission Management</h1>
        <p>권한 관리 (개인정보보호법 제5조 - 접근 권한 관리)</p>
      </div>

      {message.text && (
        <div className={message.type === 'success' ? 'success' : 'error'} style={{ marginBottom: '20px' }}>
          {message.text}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '20px' }}>
        {/* Left: Admin Users List */}
        <div className="card">
          <div className="card-header">
            <h2>Admin Users</h2>
          </div>
          
          <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
            {adminUsers.map((admin) => (
              <div
                key={admin.id}
                onClick={() => selectAdmin(admin)}
                style={{
                  padding: '12px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #ecf0f1',
                  background: selectedAdmin?.id === admin.id ? '#e8f4f8' : 'white',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (selectedAdmin?.id !== admin.id) {
                    e.target.style.background = '#f8f9fa';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedAdmin?.id !== admin.id) {
                    e.target.style.background = 'white';
                  }
                }}
              >
                <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                  {admin.username}
                </div>
                <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                  {admin.role} • {admin.email}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Permission Details */}
        <div>
          {!selectedAdmin ? (
            <div className="card">
              <div style={{ textAlign: 'center', padding: '60px', color: '#7f8c8d' }}>
                <h3>Select an admin user to manage permissions</h3>
                <p style={{ marginTop: '10px' }}>권한 관리를 위해 관리자를 선택하세요</p>
              </div>
            </div>
          ) : (
            <>
              <div className="card">
                <div className="card-header">
                  <div>
                    <h2>Permissions for {selectedAdmin.username}</h2>
                    <p style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '5px' }}>
                      Role: <span className="badge badge-info">{selectedAdmin.role}</span>
                    </p>
                  </div>
                </div>

                {userPermissions && (
                  <div>
                    <p style={{ marginBottom: '20px', color: '#7f8c8d' }}>
                      Granted: <strong>{userPermissions.granted_permissions.length}</strong> / 
                      Total: <strong>{userPermissions.available_permissions.length}</strong> permissions
                    </p>

                    {Object.keys(allPermissions).map((category) => {
                      const categoryPerms = allPermissions[category];
                      const grantedInCategory = categoryPerms.filter(p => 
                        userPermissions.granted_permissions.some(gp => gp.key === p.key)
                      ).length;

                      return (
                        <div key={category} style={{ marginBottom: '30px' }}>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginBottom: '15px',
                            paddingBottom: '10px',
                            borderBottom: '2px solid ' + (categoryColors[category] || '#95a5a6')
                          }}>
                            <h3 style={{ 
                              textTransform: 'capitalize',
                              color: categoryColors[category] || '#95a5a6',
                              margin: 0
                            }}>
                              {category.replace('_', ' ')} ({grantedInCategory}/{categoryPerms.length})
                            </h3>
                            <button
                              className="btn btn-secondary"
                              style={{ padding: '5px 10px', fontSize: '12px' }}
                              onClick={() => handleBulkGrant(category)}
                            >
                              Grant All
                            </button>
                          </div>

                          <div style={{ display: 'grid', gap: '10px' }}>
                            {categoryPerms.map((perm) => {
                              const isGranted = userPermissions.granted_permissions.some(
                                gp => gp.key === perm.key
                              );

                              return (
                                <div
                                  key={perm.key}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '12px',
                                    background: isGranted ? '#d4edda' : '#f8f9fa',
                                    borderRadius: '4px',
                                    border: '1px solid ' + (isGranted ? '#c3e6cb' : '#dee2e6')
                                  }}
                                >
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                                      {perm.name}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#6c757d' }}>
                                      {perm.description}
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#95a5a6', marginTop: '4px' }}>
                                      Key: <code>{perm.key}</code>
                                    </div>
                                  </div>
                                  <div>
                                    <label style={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      gap: '8px',
                                      cursor: 'pointer',
                                      userSelect: 'none'
                                    }}>
                                      <input
                                        type="checkbox"
                                        checked={isGranted}
                                        onChange={() => handleTogglePermission(perm.key, isGranted)}
                                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                      />
                                      <span style={{ fontSize: '14px', fontWeight: '500' }}>
                                        {isGranted ? 'Granted' : 'Not Granted'}
                                      </span>
                                    </label>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Permission Change History */}
              <div className="card" style={{ marginTop: '20px' }}>
                <div className="card-header">
                  <h2>Permission Change History</h2>
                  <p style={{ fontSize: '14px', color: '#7f8c8d', margin: '5px 0 0 0' }}>
                    권한 변경 로그 (Compliance: 제8조 - 1년 보관)
                  </p>
                </div>

                {permissionChanges.length === 0 ? (
                  <p style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
                    No permission changes recorded
                  </p>
                ) : (
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Action</th>
                          <th>Permission</th>
                          <th>Changed By</th>
                          <th>IP Address</th>
                          <th>Reason</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {permissionChanges.map((change, idx) => (
                          <tr key={idx}>
                            <td>
                              <span className={`badge ${
                                change.change_type === 'grant' ? 'badge-success' : 
                                change.change_type === 'revoke' ? 'badge-danger' : 'badge-warning'
                              }`}>
                                {change.change_type}
                              </span>
                            </td>
                            <td style={{ fontSize: '12px' }}>
                              <code>{change.permission_key}</code>
                            </td>
                            <td>{change.changed_by || '-'}</td>
                            <td style={{ fontSize: '11px' }}>{change.changed_by_ip || '-'}</td>
                            <td style={{ maxWidth: '300px', fontSize: '12px' }}>
                              {change.reason || '-'}
                            </td>
                            <td>{new Date(change.changed_at).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: '20px', background: '#fff3cd', border: '1px solid #ffc107' }}>
        <h3>🔒 Compliance Information</h3>
        <p style={{ marginTop: '10px', fontSize: '14px', lineHeight: '1.6' }}>
          <strong>개인정보보호법 제5조 (책임자의 지정)</strong><br />
          All permission changes are logged with admin identity, timestamp, IP address, and reason.
          Logs are retained for 1 year for audit purposes.
        </p>
        <p style={{ marginTop: '10px', fontSize: '14px', lineHeight: '1.6' }}>
          <strong>개인정보보호법 제8조 (개인정보의 안전성 확보조치)</strong><br />
          Permission change history is automatically maintained for compliance and security auditing.
        </p>
      </div>
    </div>
  );
}

export default Permissions;